<script lang="ts">
  import { getContext } from 'svelte';
  import type { MyFitnessStores } from '../stores';
  import { Separator } from '@/lib/components/ui/separator';
  import route from '@/Util/routes';
  import TrainingStatus from '../TrainingStatus.svelte';
  import HelpTooltip from '@/Common/HelpTooltip.svelte';

  const myFitness = getContext<MyFitnessStores>('myFitness');
  let { trainingAdvice, userParams } = myFitness

  export let athlete: string;
  export let workoutDescription: string;
  export let lastBreakthrough: {
    name: string;
    path: string;
    start_date: string;
    stale: boolean;
  };

  $: sig = $trainingAdvice?.signature;

  //@ts-ignore
  let Xert = window.Xert;

  $: profile_photo = $userParams?.profile_photo || '/assets/admin/layout/img/avatar2.png';

</script>

<aside class="w-full flex flex-col items-center">
  <img class="w-[100px] h-[100px] z-[1] rounded-lg object-cover" src={profile_photo} alt="User portrait" />
  <div class='flex flex-col w-full mt-[-50px] pt-[60px] rounded-lg border border-border bg-white p-3 shadow-sm hover:shadow'>
    <div class='flex flex-col w-full items-center'>
      <span class='text-2xl font-semibold'>{$userParams?.first_name} {$userParams?.last_name}</span>
      <!-- The athlete prop is only set on page load. Scheduling a program changes the athlete type and returns new training advice. -->
      <span class='text-base'>{$trainingAdvice?.target_athlete ?? athlete}</span>
    </div>

    <Separator class='mt-2' orientation='horizontal' />

    <div class='flex flex-col gap-2 my-6'>
      <div class='flex flex-row gap-1 items-center'>
        <TrainingStatus trainingAdvice={$trainingAdvice}/>
      </div>
  
      {#if lastBreakthrough}
        <div class='flex flex-col w-full gap-0'>
          <div class='text-sm'>Last Breakthrough:</div>
          <div class='flex flex-row gap-1 items-center'>
            <a class="underline font-medium" href="{ route('ActivityController@getActivity', [lastBreakthrough['path']])}" target="activity">
              { lastBreakthrough['name'] }
            </a>
            <span class='text-xs text-gray-500 ml-2 shrink-0 whitespace-nowrap inline-flex items-center'>
              {lastBreakthrough['start_date']}
              {#if lastBreakthrough['stale']}
              <HelpTooltip
                message="Your Fitness Signature may be out of date. It&rsquo;s been a while since your last breakthrough, so your current signature may be underestimating your fitness. Try a maximal effort or high-intensity workout to update it. <a href='https://baronbiosys.com/glossary/breakthroughs/' target='_help'>Learn more</a>"
                icon="fa fa-warning"
                label="Fitness Signature may be out of date"
                iconClass="text-amber-500"
              />
              {/if}
            </span>
          </div>
        </div>
      {/if}
    </div>

    <Separator orientation='horizontal' />

    <div class='grid grid-cols-2 grid-rows-3 w-full'>
      <div class='flex flex-col items-center justify-center col-span-2 py-1 border-border border-solid border-0 border-b'>
        <span class='flex flex-row font-bold items-baseline gap-1 text-primary'>
          <span class='text-4xl'>{sig?.ftp?.toFixed()}</span>
          <span class='text-base'>W</span>
        </span>
        <span class='text-sm font-semibold text-center'>Threshold Power</span>
      </div>

      <div class='flex flex-col items-center justify-center py-1 border-border border-solid border-0 border-b border-r'>
        <span class='flex flex-row font-bold items-baseline gap-1'>
          <span class='text-xl'>{(sig?.atc / 1000).toFixed(1)}</span>
          <span class='text-sm'>kJ</span>
        </span>
        <span class='text-xs text-center'>High Intensity Energy</span>
      </div>
      <div class='flex flex-col items-center justify-center py-1 border-border border-solid border-0 border-b'>
        <span class='flex flex-row font-bold items-baseline gap-1'>
          <span class='text-xl'>{sig?.pp?.toFixed()}</span>
          <span class='text-sm'>W</span>
        </span>
        <span class='text-xs text-center'>Peak Power</span>
      </div>
      <div class='flex flex-col items-center justify-center py-1 border-border border-solid border-0 border-r'>
        <span class='flex flex-row font-bold items-baseline gap-1'>
          <span class='text-xl'>{Xert.MyFitness.formatNumber($trainingAdvice?.current_FM, $trainingAdvice?.target_athlete_details?.per_weight)}</span>
          <span class='text-sm'>{$trainingAdvice?.target_athlete_details.per_weight ? ' W/kg' : ' W'}</span>
        </span>
        <span class='text-xs text-center'>{workoutDescription}</span>
      </div>
      <div class='flex flex-col items-center justify-center py-1'>
        <span class='flex flex-row font-bold items-baseline gap-1'>
          <span class='text-xl'>{sig?.ltp?.toFixed()}</span>
          <span class='text-sm'>W</span>
        </span>
        <span class='text-xs text-center'>Lower Threshold Power</span>
      </div>
    </div>
  </div>
</aside>
